import React, { useCallback, useEffect } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import capitalize from 'lodash.capitalize';
import useToolbarTitle from '@ifit/hooks/useToolbarTitle';

import { setLastTitle, hideModality } from '../../store/to-native';
import { useDeviceType } from '../../store/device-type';
import { getTrainerName } from '../../helpers';
import { isMobile } from '../../helpers/responsive';
import { TrainerSpotlight } from './components';
import InfiniteScrollRQ from '../../components/infinite-scroll-rq';
import GridSection from '../../components/grid-section';
import ScrollingSection from '../../components/scrolling-section';
import {
  seriesTitle,
  trainerTitle,
  workoutsTitle,
} from '../../i18n/translations';
import { getSearchState } from '../search/state/search-state';
import Loading from '@ifit/mp-loading';
import { useListProgramsInfiniteQuery } from '../../client/query/trainer/programs';
import { useTrainerGetOneQuery } from '../../client/query/trainer';
import flatten from 'lodash.flatten';
import { ILibraryContentItemType } from '@ifit/gateway-wolf-athenaeum-service';
import { useEntitiesInfiniteQuery } from '../../client/query/entities';
import getStore from '../../store';
import { getLastPageSegment } from '../search/state/on-search-page';
import { ITrainerGetResponseOK } from '@ifit/gateway-trainer-store';

const translateSectionToEntity = (
  section: string,
): [ILibraryContentItemType] => {
  switch (section) {
    case 'series':
      return [ILibraryContentItemType.Program];
    case 'workouts':
      return [ILibraryContentItemType.Workout];
    default:
      return null;
  }
};

const getSegment = (section: string): string => {
  let segment = section;
  if (!section) {
    const store = getStore();
    segment = getLastPageSegment(store);
  }

  return segment;
};

const getTitle = (trainer: ITrainerGetResponseOK, section: string): string => {
  let title = '';
  section = /\d/.test(section) ? '' : section;
  if (section === 'workouts') {
    section = workoutsTitle();
  }
  if (section === 'series') {
    section = seriesTitle();
  }
  if (trainer) {
    title = [getTrainerName(trainer) || trainerTitle(), capitalize(section)]
      .filter(Boolean)
      .join(' ');
  }

  return title;
};

const TrainerProfile = () => {
  const { trainerId, section }: any = useParams();
  const baseRoute = '/trainer/:trainerId';
  const deviceType = useDeviceType();

  const segment = getSegment(section);
  const sectionEntity = translateSectionToEntity(segment);

  useEffect(() => {
    if (!isMobile(deviceType)) {
      hideModality();
    }
    getSearchState().setViewContext({
      trainerIds: [trainerId],
      includedEntities: sectionEntity,
    });
  }, [trainerId, sectionEntity, deviceType]);
  const { data, isLoading } = useTrainerGetOneQuery(trainerId);
  const trainer = data;

  const title = getTitle(trainer, segment);

  useToolbarTitle(
    title,
    {
      buttonStyle: 'back',
      searchIsVisible: false,
    },
    () => setLastTitle(title),
  );

  const {
    isLoading: loadingSeries,
    isFetching: fetchingSeries,
    fetchNextPage: fetchNextSeriesPage,
    data: { pages: series = [] } = {},
  } = useListProgramsInfiniteQuery(trainerId);
  const seriesItems = flatten(series.map(s => s.data));
  const hasMoreSeries =
    series && series.length && series[series.length - 1].hasMoreItems;

  const loadMoreSeries = useCallback(() => fetchNextSeriesPage(), [
    fetchNextSeriesPage,
  ]);

  const {
    dataMemo: { data: workoutItems = [], hasMoreItems: hasMoreWorkouts },
    query: { isLoading: loadingWorkouts, isFetching: fetchingWorkouts },
    fetchNextPageCallback: fetchNextWorkoutsPage,
  } = useEntitiesInfiniteQuery({
    trainerIds: trainerId,
    includedEntities: ['workout'],
  });

  const loadMoreWorkouts = useCallback(() => fetchNextWorkoutsPage(), [
    fetchNextWorkoutsPage,
  ]);

  return (
    <Switch>
      <Route exact path={baseRoute}>
        <div className="trainer-view">
          <TrainerSpotlight loading={isLoading} trainer={trainer} />
          <ScrollingSection
            data-testid="series"
            title={seriesTitle()}
            isLoading={loadingSeries}
            listData={{
              hasMoreItems: seriesItems.length > 10,
              data: seriesItems.slice(0, 10),
            }}
            route={`/trainer/${trainerId}/series`}
          />
          <ScrollingSection
            data-testid="workouts"
            title={workoutsTitle()}
            isLoading={loadingWorkouts}
            listData={{
              data: workoutItems.slice(0, 10),
              hasMoreItems: workoutItems.length > 10,
            }}
            route={`/trainer/${trainerId}/workouts`}
          />
        </div>
      </Route>
      <Route path={`${baseRoute}/workouts`}>
        {loadingWorkouts && (
          <Loading zIndex={11} data-testid={'loading-spinner'} />
        )}
        <InfiniteScrollRQ
          libraryCards={workoutItems}
          loadMore={loadMoreWorkouts}
          hasMoreItems={hasMoreWorkouts}
          isFetching={fetchingWorkouts}
        >
          <GridSection disableAvatars />
        </InfiniteScrollRQ>
      </Route>
      <Route path={`${baseRoute}/series`}>
        {loadingSeries && <Loading zIndex={11} />}
        <InfiniteScrollRQ
          libraryCards={seriesItems}
          loadMore={loadMoreSeries}
          hasMoreItems={hasMoreSeries}
          isFetching={fetchingSeries}
        >
          <GridSection disableAvatars />
        </InfiniteScrollRQ>
      </Route>
    </Switch>
  );
};

export default TrainerProfile;
